import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { toRgbChannels } from '@/lib/hero-visual/color'
import { createHeroVisual } from '@/lib/hero-visual/engine'

import { HERO_VISUAL } from '@/constants/hero-visual'

interface HeroVisualProps {
    className?: string
}

const paletteStyle = {
    '--hv-bg': toRgbChannels(HERO_VISUAL.colors.background),
    '--hv-cyan': toRgbChannels(HERO_VISUAL.colors.cyan),
    '--hv-teal': toRgbChannels(HERO_VISUAL.colors.teal),
    '--hv-ice': toRgbChannels(HERO_VISUAL.colors.ice),
    '--hv-frost': toRgbChannels(HERO_VISUAL.colors.frost)
} as CSSProperties

const HeroVisual: FC<HeroVisualProps> = ({ className }): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const backRef = useRef<HTMLCanvasElement>(null)
    const frontRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const root = rootRef.current
        const back = backRef.current
        const front = frontRef.current

        if (!root || !back || !front) return

        const glass = Array.from(root.querySelectorAll<HTMLElement>('[data-glass]'))
        const visual = createHeroVisual({ root, back, front, glass }, HERO_VISUAL)

        return () => visual.destroy()
    }, [])

    return (
        <div
            ref={rootRef}
            aria-hidden='true'
            className={cn('hero-visual', className)}
            style={paletteStyle}
        >
            <canvas ref={backRef} className='hero-visual__canvas' />

            {HERO_VISUAL.glass.map((shape) => (
                <div
                    key={shape.id}
                    data-glass={shape.id}
                    className={cn('hero-glass', `hero-glass--${shape.kind}`)}
                    style={{
                        WebkitBackdropFilter: `blur(${shape.blur}px) saturate(160%) brightness(116%)`,
                        backdropFilter: `blur(${shape.blur}px) saturate(160%) brightness(116%)`
                    }}
                />
            ))}

            <canvas ref={frontRef} className='hero-visual__canvas' />

            <div className='hero-visual__scrim' />
        </div>
    )
}

export default HeroVisual
